/**
 * Mock data generator for Probabilistic Forecast component
 */

export const generateMockForecastData = (config) => {
  const { mockData } = config;
  const { dataPoints, baseValues, timePattern } = mockData;
  
  const data = [];
  const now = new Date();
  const startTime = new Date(now.getTime() - (dataPoints * timePattern.intervalMinutes * 60 * 1000));
  
  for (let i = 0; i < dataPoints; i++) {
    const currentTime = new Date(startTime.getTime() + (i * timePattern.intervalMinutes * 60 * 1000));
    const hour = currentTime.getHours();
    
    // Calculate solar pattern factor (higher during peak hours)
    const solarFactor = calculateSolarFactor(hour, timePattern);
    
    // Generate realistic power values with solar pattern
    const actualPower = generateRealisticValue(baseValues.actualPower, solarFactor);
    const p10 = generateRealisticValue(baseValues.p10, solarFactor);
    const p50 = generateRealisticValue(baseValues.p50, solarFactor);
    const p90 = generateRealisticValue(baseValues.p90, solarFactor);
    
    data.push({
      date: currentTime.toISOString(),
      actual_power_kw: actualPower.toFixed(2),
      predicted_p10_kw: p10.toFixed(2),
      predicted_p50_kw: p50.toFixed(2),
      predicted_p90_kw: p90.toFixed(2),
      timestamp: currentTime.getTime()
    });
  }
  
  return data;
};

/**
 * Calculate solar generation factor based on time of day
 */
const calculateSolarFactor = (hour, timePattern) => {
  const { startHour, peakHour, endHour } = timePattern;
  
  if (hour < startHour || hour > endHour) {
    return 0.1; // Very low generation during night
  }
  
  if (hour <= peakHour) {
    // Ramp up to peak
    return 0.1 + (0.9 * (hour - startHour) / (peakHour - startHour));
  } else {
    // Ramp down from peak
    return 1.0 - (0.9 * (hour - peakHour) / (endHour - peakHour));
  }
};

/**
 * Generate realistic power values with variance and solar pattern
 */
const generateRealisticValue = (baseConfig, solarFactor) => {
  const { min, max, variance } = baseConfig;
  const baseValue = min + (max - min) * solarFactor;
  const varianceAmount = (Math.random() - 0.5) * variance;
  return Math.max(0, baseValue + varianceAmount);
};

/**
 * Generate sample response structure matching API
 */
export const generateMockApiResponse = (config) => {
  const data = generateMockForecastData(config);
  
  return {
    data,
    metadata: {
      total: data.length,
      lastUpdated: new Date().toISOString(),
      dataSource: "mock",
      version: "1.0"
    },
    status: "success"
  };
};

/**
 * Simulate API delay for realistic testing
 */
export const mockApiCall = async (config, delay = 1000) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(generateMockApiResponse(config));
    }, delay);
  });
};
